import { Link } from "react-router-dom";
import { Stethoscope, ListChecks, CalendarCheck2 } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useSeo, pageSeo, PAGES, clinicSchema, medicalPageSchema, graph, crumbs } from "@/lib/seo";
import { CONDITIONS } from "@/data/conditions";
import { FACTORS_NOTE, CARE_APPROACH } from "@/data/copy";
import { PageHero } from "@/components/common/SectionHeading";
import { UrgentCareBox } from "@/components/common/UrgentCareBox";
import { CtaBand } from "@/components/common/CtaBand";
import { accent } from "@/lib/ui";

const Card = ({ icon: Icon, title, tone, children }) => (
  <div className="rounded-3xl bg-white p-6 shadow-[0_18px_40px_-34px_rgba(1,78,150,0.5)] ring-1 ring-slate-200/80">
    <h3 className="flex items-center gap-3 text-base font-bold text-brand-ink">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon className="h-4 w-4" aria-hidden="true" /></span> {title}
    </h3>
    <div className="mt-3 text-base leading-relaxed text-slate-700">{children}</div>
  </div>
);

const ConditionSection = ({ c, i }) => {
  const Icon = c.icon;
  return (
    <section id={c.id} className={`section scroll-mt-24 ${i % 2 ? "bg-gradient-to-br from-brand-tint via-white to-brand-greenTint" : ""}`} aria-labelledby={`${c.id}-title`} data-testid={`condition-section-${c.id}`}>
      <div className="container-x grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className={`flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg ${accent(i).icon}`}><Icon className="h-7 w-7" aria-hidden="true" /></span>
          <h2 id={`${c.id}-title`} className="mt-5 font-heading text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">{c.title}</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-700 md:text-lg">{c.overview}</p>
        </div>
        <div className="grid gap-4 lg:col-span-7">
          <Card icon={ListChecks} title="Common contributing factors" tone="bg-brand-warmTint text-brand-coralDeep">
            <ul className="list-disc space-y-1.5 pl-5 marker:text-brand-coral">{c.factors.map((f) => <li key={f}>{f}</li>)}</ul>
          </Card>
          <Card icon={Stethoscope} title="How Young Spine approaches it" tone="bg-brand-tint2 text-brand-blue">{c.approach}</Card>
          <Card icon={CalendarCheck2} title="When to see a doctor" tone="bg-brand-greenTint text-brand-greenDeep">{c.when}</Card>
        </div>
      </div>
    </section>
  );
};

export default function Conditions() {
  const { settings } = useSite();
  const meta = pageSeo("/conditions");
  useSeo({
    ...meta,
    jsonLd: graph(clinicSchema(settings), medicalPageSchema("/conditions", meta.title, meta.description, CONDITIONS), crumbs(["Conditions", "/conditions"])),
  });
  return (
    <>
      <PageHero eyebrow="Conditions" title={PAGES["/conditions"].h1} intro={CARE_APPROACH} />
      <nav aria-label="Conditions on this page" className="sticky top-16 z-30 border-b border-slate-200 bg-white/95 backdrop-blur-md lg:top-20">
        <ul className="container-x flex gap-2 overflow-x-auto py-3 no-scrollbar">
          {CONDITIONS.map((c, i) => (
            <li key={c.id} className="shrink-0">
              <Link to={`#${c.id}`} data-testid={`condition-chip-${c.id}`} className={`block rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors ${accent(i).chip}`}>{c.tile}</Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="container-x pt-12">
        <UrgentCareBox />
        <p className="mt-6 text-base text-slate-600">{FACTORS_NOTE} The information below is general and is not a diagnosis — please see a doctor for advice about your own symptoms.</p>
      </div>
      {CONDITIONS.map((c, i) => <ConditionSection key={c.id} c={c} i={i} />)}
      <CtaBand title="Talk to us about your symptoms" />
    </>
  );
}
